import React, { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { useDock, useVisibleDockItems } from './DockContext';
import { DockItem } from './DockItem';
import { DockDrawer } from './DockDrawer';
import './dock.css';

/**
 * Expand button icon component
 * Shows a grid icon that indicates more items are available
 */
function ExpandIcon(): JSX.Element {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="5" cy="5" r="1.5" fill="currentColor" />
      <circle cx="12" cy="5" r="1.5" fill="currentColor" />
      <circle cx="19" cy="5" r="1.5" fill="currentColor" />
      <circle cx="5" cy="12" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" />
      <circle cx="5" cy="19" r="1.5" fill="currentColor" />
      <circle cx="12" cy="19" r="1.5" fill="currentColor" />
      <circle cx="19" cy="19" r="1.5" fill="currentColor" />
    </svg>
  );
}

/**
 * ECG visualization component for dock background
 * Displays a subtle animated ECG line moving across the dock
 * Path uses viewBox height of 100, with baseline at 50
 */
const ECG_PATH = `
  M0,50
  L35,50
  L40,48 L45,50 L50,52 L55,50
  L60,50 L65,10 L70,90 L75,50
  L130,50
  L135,48 L140,50 L145,52 L150,50
  L155,50 L160,10 L165,90 L170,50
  L225,50
  L230,48 L235,50 L240,52 L245,50
  L250,50 L255,10 L260,90 L265,50
  L320,50
  L325,48 L330,50 L335,52 L340,50
  L345,50 L350,10 L355,90 L360,50
  L415,50
  L420,48 L425,50 L430,52 L435,50
  L440,50 L445,10 L450,90 L455,50
  L510,50
  L515,48 L520,50 L525,52 L530,50
  L535,50 L540,50
`;

function DockEcg(): JSX.Element {
  return (
    <div className="dock-ecg-container" aria-hidden="true">
      <svg viewBox="0 0 540 100" preserveAspectRatio="xMidYMid meet">
        {/* Base ECG path - always visible */}
        <path className="dock-ecg-path-base" d={ECG_PATH} />
        {/* Animated tracing path - moving highlight */}
        <path className="dock-ecg-path-trace" d={ECG_PATH} />
      </svg>
    </div>
  );
}

/**
 * Calculate magnification scale based on distance from hovered item
 * Creates a smooth falloff effect like macOS dock
 */
function calculateMagnification(
  hoveredIndex: number,
  currentIndex: number,
  hoverScale: number,
  neighborScale: number,
  distantScale: number
): number {
  if (hoveredIndex === -1) {return 1;}

  const distance = Math.abs(hoveredIndex - currentIndex);

  if (distance === 0) {return hoverScale;}
  if (distance === 1) {return neighborScale;}
  if (distance === 2) {return distantScale;}

  return 1;
}

/**
 * Main Dock component
 * Renders a fixed dock at the bottom center of the screen
 * with visible items and an expand button to access the drawer
 */
export function Dock(): JSX.Element {
  const { isDrawerOpen, toggleDrawer, triggerAction, setDockHover, setDockDimensions } = useDock();
  const visibleItems = useVisibleDockItems();
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
  const dockItemsRef = useRef<HTMLDivElement>(null);
  const dockContainerRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Magnification scale values from CSS custom properties
  const magnificationConfig = useMemo(() => ({
    hoverScale: 1.2,
    neighborScale: 1.1,
    distantScale: 1.05,
  }), []);

  // Measure dock dimensions on mount and resize
  useEffect(() => {
    const measureDock = (): void => {
      if (dockContainerRef.current) {
        const rect = dockContainerRef.current.getBoundingClientRect();
        setDockDimensions(rect.width, rect.height);
      }
    };

    // Initial measurement after layout
    requestAnimationFrame(measureDock);

    // Remeasure on resize
    window.addEventListener('resize', measureDock);
    return () => window.removeEventListener('resize', measureDock);
  }, [setDockDimensions]);

  // Dock hover handlers for panel coordination
  const handleDockMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setDockHover(true);
  }, [setDockHover]);

  const handleDockMouseLeave = useCallback(() => {
    // Small delay to prevent glitches from dock item transforms
    hoverTimeoutRef.current = setTimeout(() => {
      setDockHover(false);
    }, 50);
  }, [setDockHover]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleItemClick = useCallback(
    (action: string) => {
      triggerAction(action as Parameters<typeof triggerAction>[0]);
    },
    [triggerAction]
  );

  const handleExpandClick = useCallback(() => {
    toggleDrawer();
  }, [toggleDrawer]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleDrawer();
      }
    },
    [toggleDrawer]
  );

  const handleItemHover = useCallback((index: number) => {
    setHoveredIndex(index);
  }, []);

  const handleItemLeave = useCallback(() => {
    setHoveredIndex(-1);
  }, []);

  // Calculate magnification scales for all visible items
  const magnificationScales = useMemo(() => {
    return visibleItems.map((_, index) =>
      calculateMagnification(
        hoveredIndex,
        index,
        magnificationConfig.hoverScale,
        magnificationConfig.neighborScale,
        magnificationConfig.distantScale
      )
    );
  }, [hoveredIndex, visibleItems, magnificationConfig]);

  return (
    <>
      <nav
        className="dock"
        role="navigation"
        aria-label="Main dock"
        onMouseEnter={handleDockMouseEnter}
        onMouseLeave={handleDockMouseLeave}
      >
        <div className="dock-container" ref={dockContainerRef}>
          {/* Road interior - asphalt surface with headlight glow */}
          <div className="dock-road-interior" aria-hidden="true">
            <div className="dock-road-headlight" />
          </div>

          {/* Road edge stripes - shoulder markings */}
          <div className="dock-road-edge dock-road-edge--left" aria-hidden="true" />
          <div className="dock-road-edge dock-road-edge--right" aria-hidden="true" />

          {/* ECG visualization (hidden by CSS - replaced by road) */}
          <DockEcg />

          {/* Glass highlight effect for light refraction */}
          <div className="dock-glass-highlight" aria-hidden="true" />

          {/* Visible dock items with lane dividers */}
          <div
            ref={dockItemsRef}
            className="dock-items"
            role="toolbar"
            aria-label="Dock items"
            onMouseLeave={handleItemLeave}
          >
            {visibleItems.map((item, index) => (
              <React.Fragment key={item.id}>
                {/* Lane divider before each item except the first */}
                {index > 0 && (
                  <div className="dock-lane-divider" aria-hidden="true" />
                )}
                <DockItem
                  item={item}
                  onClick={() => handleItemClick(item.action)}
                  magnificationScale={magnificationScales[index]}
                  onHover={() => handleItemHover(index)}
                  onLeave={handleItemLeave}
                />
              </React.Fragment>
            ))}

            {/* Lane divider before expand button */}
            <div className="dock-lane-divider" aria-hidden="true" />

            {/* Expand button - always in last position */}
            <button
              type="button"
              className={`dock-item-button dock-expand-button ${isDrawerOpen ? 'dock-expand-button--active' : ''}`}
              onClick={handleExpandClick}
              onKeyDown={handleKeyDown}
              aria-label={isDrawerOpen ? 'Close dock drawer' : 'Open dock drawer'}
              aria-expanded={isDrawerOpen}
              aria-controls="dock-drawer"
            >
              <span className="dock-item-icon">
                <ExpandIcon />
              </span>
            </button>
          </div>

          {/* Reflection effect - mirror of icons below dock */}
          <div className="dock-reflection" aria-hidden="true">
            <div className="dock-reflection-content">
              {visibleItems.map((item) => (
                <div key={`reflection-${item.id}`} className="dock-reflection-item">
                  {item.icon}
                </div>
              ))}
              {/* Expand button reflection */}
              <div className="dock-reflection-item">
                <ExpandIcon />
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Drawer component */}
      <DockDrawer />
    </>
  );
}

export default Dock;
